import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import { Camera, RefreshCw, Loader2, RotateCcw, Trash2, Eye, Download, Clock, Shield } from 'lucide-react';

const SnapshotsPage = () => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [viewSnapshot, setViewSnapshot] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const fetchSnapshots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/stock/snapshots');
      setSnapshots(res.data || []);
    } catch {
      toast.error('Erreur chargement des sauvegardes');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSnapshots(); }, [fetchSnapshots]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await api.post('/stock/snapshot');
      toast.success(`Sauvegarde creee : ${res.data.total_products} produits, ${res.data.total_quantity} pieces`);
      fetchSnapshots();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur creation sauvegarde'));
    }
    setCreating(false);
  };

  const handleRestore = async (snap) => {
    if (!window.confirm(`Restaurer le stock au ${new Date(snap.created_at).toLocaleDateString('fr-FR')} ?\n\nLe stock actuel sera sauvegarde automatiquement avant la restauration.`)) return;
    setRestoring(snap.id);
    try {
      const res = await api.post(`/stock/snapshots/${snap.id}/restore`);
      toast.success(`Stock restaure : ${res.data.restored} produits remis en place`);
      fetchSnapshots();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur restauration'));
    }
    setRestoring(null);
  };

  const handleDelete = async (snapId) => {
    if (!window.confirm('Supprimer cette sauvegarde ?')) return;
    try {
      await api.delete(`/stock/snapshots/${snapId}`);
      toast.success('Sauvegarde supprimee');
      fetchSnapshots();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur suppression'));
    }
  };

  const handleView = async (snap) => {
    setViewSnapshot(snap);
    setViewLoading(true);
    try {
      const res = await api.get(`/stock/snapshots/${snap.id}`);
      setViewData(res.data);
    } catch {
      toast.error('Erreur chargement du detail');
    }
    setViewLoading(false);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'manual': return <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-[10px]">Manuel</Badge>;
      case 'auto': return <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">Auto</Badge>;
      case 'auto_pre_restore': return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">Pre-restauration</Badge>;
      default: return <Badge variant="outline" className="text-[10px]">{type}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="snapshots-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
            <Camera className="w-6 h-6 text-cyan-400" />
            Sauvegardes Stock
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Photographiez vos stocks et restaurez en cas de probleme</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchSnapshots} variant="outline" size="sm" className="border-zinc-700 text-zinc-300" data-testid="refresh-snapshots">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
          <Button onClick={handleCreate} disabled={creating} size="sm" className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="create-snapshot">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
            Sauvegarder maintenant
          </Button>
        </div>
      </div>

      {/* Info */}
      <Card className="bg-cyan-500/5 border-cyan-500/20">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-white text-sm font-medium">Sauvegarde automatique tous les 15 jours</p>
            <p className="text-zinc-400 text-xs mt-1">
              Chaque sauvegarde enregistre la totalite des stocks (quantites, depots, produits).
              En cas de probleme, restaurez en un clic. Le stock actuel est automatiquement sauvegarde avant toute restauration.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Snapshots List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-zinc-400" /> Historique des sauvegardes ({snapshots.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
          ) : snapshots.length === 0 ? (
            <div className="text-center py-12">
              <Camera className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">Aucune sauvegarde</p>
              <p className="text-zinc-600 text-xs mt-1">Cliquez "Sauvegarder maintenant" pour creer votre premiere sauvegarde</p>
            </div>
          ) : (
            <div className="space-y-2">
              {snapshots.map((snap, idx) => (
                <div key={snap.id} className={`flex items-center justify-between p-3 rounded-lg border ${idx === 0 ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-zinc-950 border-zinc-800'}`}
                  data-testid={`snapshot-${snap.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Camera className={`w-5 h-5 ${idx === 0 ? 'text-cyan-400' : 'text-zinc-500'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-medium">{formatDate(snap.created_at)}</p>
                        {getTypeBadge(snap.type)}
                        {idx === 0 && <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">Derniere</Badge>}
                      </div>
                      <p className="text-zinc-500 text-xs mt-0.5">
                        {snap.total_products} produits — {snap.total_quantity} pieces
                        {snap.created_by_name && <span className="ml-2">par {snap.created_by_name}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" className="h-8 text-xs border-zinc-700 text-zinc-300"
                      onClick={() => handleView(snap)} data-testid={`view-${snap.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Detail
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                      onClick={() => handleRestore(snap)} disabled={restoring === snap.id}
                      data-testid={`restore-${snap.id}`}>
                      {restoring === snap.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                      Restaurer
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs text-zinc-500 hover:text-red-400"
                      onClick={() => handleDelete(snap.id)} data-testid={`delete-${snap.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Snapshot Dialog */}
      <Dialog open={!!viewSnapshot} onOpenChange={() => { setViewSnapshot(null); setViewData(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              Sauvegarde du {viewSnapshot ? formatDate(viewSnapshot.created_at) : ''}
            </DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
          ) : viewData ? (
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="bg-zinc-950 rounded-lg p-3 flex-1 border border-zinc-800">
                  <p className="text-zinc-500 text-xs">Produits</p>
                  <p className="text-2xl text-white font-bold">{viewData.total_products}</p>
                </div>
                <div className="bg-zinc-950 rounded-lg p-3 flex-1 border border-zinc-800">
                  <p className="text-zinc-500 text-xs">Pieces totales</p>
                  <p className="text-2xl text-cyan-400 font-bold">{viewData.total_quantity}</p>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Reference</TableHead>
                    <TableHead className="text-zinc-400">Designation</TableHead>
                    <TableHead className="text-zinc-400">Classe</TableHead>
                    <TableHead className="text-zinc-400 text-right">Quantite</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(viewData.items || []).map((item, i) => (
                    <TableRow key={i} className="border-zinc-800">
                      <TableCell className="font-mono text-cyan-400 text-xs">{item.reference || item.product_id?.substring(0, 12)}</TableCell>
                      <TableCell className="text-white text-sm">{item.designation || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{item.risk_class || '-'}</Badge></TableCell>
                      <TableCell className="text-right font-mono text-amber-400">{item.available_quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SnapshotsPage;
